/**
 * Booking Store
 * Manages booking data and state
 * 
 * @author Varun Pandey
 */

import { create } from 'zustand';
import type { Booking } from '@/api/types';

interface BookingState {
  bookings: Booking[];
  selectedBooking: Booking | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setBookings: (bookings: Booking[]) => void;
  addBooking: (booking: Booking) => void;
  updateBooking: (bookingId: string, updates: Partial<Booking>) => void;
  setSelectedBooking: (booking: Booking | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearBookings: () => void;
}

export const useBookingStore = create<BookingState>()((set) => ({
  bookings: [],
  selectedBooking: null,
  isLoading: false,
  error: null,

  setBookings: (bookings) => set({ bookings }),

  addBooking: (booking) => {
    set((state) => ({
      bookings: [booking, ...state.bookings],
    }));
  },

  updateBooking: (bookingId, updates) => {
    set((state) => ({
      bookings: state.bookings.map((booking) =>
        booking.id === bookingId ? { ...booking, ...updates } : booking
      ),
      selectedBooking:
        state.selectedBooking?.id === bookingId
          ? { ...state.selectedBooking, ...updates }
          : state.selectedBooking,
    }));
  },

  setSelectedBooking: (booking) => set({ selectedBooking: booking }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  clearBookings: () => {
    set({
      bookings: [],
      selectedBooking: null,
      error: null,
    });
  },
}));
